import React, { useState } from 'react';
import { 
  Database, 
  Download, 
  Copy, 
  Check, 
  X, 
  ShieldCheck, 
  Server, 
  UploadCloud,
  UserCheck,
  LogIn,
  LogOut,
  Wifi,
  WifiOff,
  AlertCircle
} from 'lucide-react';
import { FamilyMember, Story, ArchiveItem } from '../types';
import { useAuth } from '../context/AuthContext';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { doc, setDoc } from 'firebase/firestore';

interface FirebaseExportModalProps {
  members: FamilyMember[];
  stories: Story[];
  archiveItems: ArchiveItem[];
  onClose: () => void;
}

export const FirebaseExportModal: React.FC<FirebaseExportModalProps> = ({
  members,
  stories,
  archiveItems,
  onClose
}) => {
  const { user, isConnected, signIn, signOut } = useAuth();
  const [copiedRules, setCopiedRules] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const exportDataJson = JSON.stringify(
    {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      familyTree: members,
      stories: stories,
      archiveVault: archiveItems
    },
    null,
    2
  );

  const handleDownloadBackup = () => {
    const blob = new Blob([exportDataJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Family_Archive_Backup_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSyncToFirestore = async () => {
    if (!user) {
      setSyncError('Please sign in with Google to sync with Cloud Firestore.');
      return;
    }
    setSyncing(true);
    setSyncError(null);
    setSyncSuccess(null);

    try {
      // Sync members to Firestore
      for (const m of members) {
        const path = `members/${m.id}`;
        try {
          await setDoc(doc(db, 'members', m.id), {
            id: m.id,
            firstName: m.firstName,
            lastName: m.lastName,
            occupation: m.occupation || '',
            birthYear: m.birthYear || null,
            deathYear: m.deathYear || null,
            bio: m.bio || '',
            photoUrl: m.photoUrl || '',
            updatedAt: new Date().toISOString(),
          }, { merge: true });
        } catch (err) {
          handleFirestoreError(err, OperationType.WRITE, path);
        }
      }

      // Sync stories to Firestore
      for (const s of stories) {
        const path = `stories/${s.id}`;
        try {
          await setDoc(doc(db, 'stories', s.id), {
            id: s.id,
            title: s.title,
            year: s.year || null,
            decade: s.decade || '',
            content: s.content || '',
            authorName: s.authorName || '',
            submittedBy: user.uid,
            updatedAt: new Date().toISOString(),
          }, { merge: true });
        } catch (err) {
          handleFirestoreError(err, OperationType.WRITE, path);
        }
      }

      setSyncSuccess(`Successfully synced ${members.length} relatives and ${stories.length} stories to Firestore!`);
    } catch (err) {
      console.error('Sync error:', err);
      setSyncError('Failed to sync documents to Firestore. Check connection or permissions.');
    } finally {
      setSyncing(false);
    }
  };

  const sampleSecurityRules = `rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /{document=**} {
      allow read, write: if false;
    }
    match /members/{memberId} {
      allow read: if true;
      allow write: if request.auth != null;
    }
    match /stories/{storyId} {
      allow read: if true;
      allow create: if request.auth != null;
      allow update, delete: if request.auth != null;
    }
    match /research_notes/{noteId} {
      allow read, write: if request.auth != null && resource.data.userId == request.auth.uid;
    }
  }
}`;

  const handleCopyRules = () => {
    navigator.clipboard.writeText(sampleSecurityRules);
    setCopiedRules(true);
    setTimeout(() => setCopiedRules(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-xs flex items-center justify-center p-3 md:p-6 overflow-y-auto">
      <div className="bg-[#FAF8F5] border-4 border-[#2C221E] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative text-[#2C221E]">
        
        {/* Header */}
        <div className="bg-[#2C221E] text-white p-3.5 sm:p-5 md:p-6 sticky top-0 z-10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <Database className="w-6 h-6 text-amber-400 shrink-0" />
            <div className="min-w-0">
              <h3 className="font-serif text-lg sm:text-2xl font-bold text-white truncate">
                Firebase Cloud Database &amp; Auth
              </h3>
              <p className="text-[11px] sm:text-xs text-amber-200/90 truncate">
                Project ID: <span className="font-mono font-semibold">familytreeapp-64581</span>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] p-2 bg-white/20 hover:bg-red-600 text-white rounded-xl transition flex items-center justify-center cursor-pointer shrink-0"
            aria-label="Close Firebase modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 md:p-8 space-y-5 sm:space-y-6">
          
          {/* Cloud Connection & Authentication Bar */}
          <div className="bg-white p-4 sm:p-5 rounded-xl border-2 border-[#D8CFC4] flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`p-2.5 rounded-xl shrink-0 ${isConnected ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                {isConnected ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
              </div>
              <div className="min-w-0">
                <h4 className="font-bold text-xs sm:text-sm text-[#2C221E] flex flex-wrap items-center gap-1.5">
                  <span>Firestore Status:</span>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-semibold ${isConnected ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-200 text-stone-700'}`}>
                    {isConnected ? 'Connected & Active' : 'Initializing...'}
                  </span>
                </h4>
                <p className="text-[11px] sm:text-xs text-[#5c4328] truncate">
                  {user ? `Signed in as ${user.displayName || user.email}` : 'Sign in with Google to enable cloud database persistence'}
                </p>
              </div>
            </div>

            <div className="shrink-0 self-end sm:self-auto">
              {user ? (
                <button
                  onClick={signOut}
                  className="min-h-[44px] px-3.5 py-2 bg-stone-200 hover:bg-stone-300 text-[#2C221E] rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              ) : (
                <button
                  onClick={signIn}
                  className="min-h-[44px] px-4 py-2 bg-[#7a5b3a] hover:bg-[#5c4328] text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-xs cursor-pointer"
                >
                  <LogIn className="w-4 h-4 text-amber-300" />
                  <span>Sign In with Google</span>
                </button>
              )}
            </div>
          </div>

          {/* Cloud Sync Action Card */}
          <div className="bg-white p-4 sm:p-6 rounded-xl border-2 border-[#D8CFC4] space-y-3.5 sm:space-y-4 shadow-xs">
            <div className="flex items-center justify-between">
              <h4 className="font-serif text-lg sm:text-xl font-bold text-[#8B263E] flex items-center gap-2">
                <UploadCloud className="w-5 h-5 text-[#8B263E] shrink-0" />
                <span>Sync Archive to Cloud Firestore</span>
              </h4>
              <span className="text-[11px] sm:text-xs bg-[#F2ECE1] text-[#2C221E] font-bold px-2.5 py-1 rounded">
                Cloud Sync
              </span>
            </div>

            <p className="text-xs sm:text-sm text-[#4A3E38]">
              Upload all local family members ({members.length}) and historical stories ({stories.length}) directly into your Firebase project's Firestore database.
            </p>

            {syncSuccess && (
              <div className="p-3 bg-emerald-50 border border-emerald-300 text-emerald-900 rounded-lg text-xs flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-700 shrink-0" />
                <span>{syncSuccess}</span>
              </div>
            )}

            {syncError && (
              <div className="p-3 bg-red-50 border border-red-300 text-red-900 rounded-lg text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-700 shrink-0" />
                <span>{syncError}</span>
              </div>
            )}

            <div className="flex flex-col sm:flex-row flex-wrap gap-2.5 sm:gap-3">
              <button
                onClick={handleSyncToFirestore}
                disabled={syncing}
                className="min-h-[44px] px-4 sm:px-5 py-2.5 sm:py-3 bg-[#7a5b3a] text-white font-bold text-xs sm:text-sm rounded-xl hover:bg-[#5c4328] disabled:opacity-50 transition shadow-xs flex items-center justify-center gap-2 cursor-pointer"
              >
                <UploadCloud className={`w-4 h-4 ${syncing ? 'animate-bounce' : ''}`} />
                <span>{syncing ? 'Syncing to Firestore...' : 'Sync to Cloud Firestore'}</span>
              </button>

              <button
                onClick={handleDownloadBackup}
                className="min-h-[44px] px-4 sm:px-5 py-2.5 sm:py-3 bg-white border-2 border-[#7a5b3a] text-[#7a5b3a] font-bold text-xs sm:text-sm rounded-xl hover:bg-[#f3ede0] transition flex items-center justify-center gap-2 cursor-pointer"
              >
                <Download className="w-4 h-4" />
                <span>Download JSON Backup</span>
              </button>
            </div>
          </div>

          {/* Firestore Security Rules Preview */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-serif text-base font-bold text-[#2C221E] flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-700" />
                <span>Deployed Firestore Security Rules (firestore.rules)</span>
              </h4>
              <button
                onClick={handleCopyRules}
                className="px-3 py-1 bg-white border border-[#D8CFC4] rounded text-xs font-bold text-[#8B263E] hover:bg-[#8B263E] hover:text-white transition flex items-center gap-1 cursor-pointer"
              >
                {copiedRules ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedRules ? 'Copied Rules!' : 'Copy Rules'}</span>
              </button>
            </div>

            <pre className="p-4 bg-[#2C221E] text-amber-100 rounded-xl font-mono text-xs overflow-x-auto border border-[#D8CFC4]">
              {sampleSecurityRules}
            </pre>
          </div>

        </div>

      </div>
    </div>
  );
};
